import { api } from "./client";
import type { CollectionItemStatus, CollectionPackageStatus } from "./collectionPackages";

// External Intake & Collection v1.0 PR 7 — staff staging review queue +
// approve-and-file. Mirrors the backend DTOs in
// PhoenixCapital.Core/DTOs/Collection/CollectionDtos.cs and the routes
// on PhoenixCapital.Api/Controllers/Collection/CollectionStagingController.cs.

export type ScanStatus = "Pending" | "Clean" | "Infected" | "Error";
// "Unconfirmed" (review loop) is an upload the recipient staged but has not
// confirmed; the server never lists it to staff, but the union stays exhaustive.
export type StagedReviewStatus =
  | "AwaitingReview"
  | "Approved"
  | "Returned"
  | "Superseded"
  | "Unconfirmed";
// Review loop — why an AwaitingReview row is back in review.
export type StagedReviewMarker = "Resubmitted" | "Reopened";
export type CollectionReviewEventKind =
  | "Submitted"
  | "Returned"
  | "Reopened"
  | "ResentForRemoteSigning"
  | "Approved"
  | "Filed";

// Review loop — one Review history entry. Staff actors are named by
// actorUserId and resolved with useInternalUserLabels; a recipient actor's
// name arrives resolved in actorName. Never render either id.
export interface CollectionReviewEvent {
  id: string;
  kind: CollectionReviewEventKind;
  stagedSubmissionId: string | null;
  actorUserId: string | null;
  actorName: string | null;
  note: string | null;
  createdAt: string;
  folderName?: string | null;
  withoutChanges?: boolean;
  alreadyFiled?: boolean;
}
export type AssessmentRunStatus = "Pending" | "Completed" | "Failed" | "Skipped";

export interface CollectionReturnComment {
  id: string;
  stagedSubmissionId: string;
  reviewerId: string | null;
  note: string;
  createdAt: string;
}

export interface StagedSubmissionListItem {
  id: string;
  collectionItemId: string;
  packageId: string;
  ownerEntityType: string;
  ownerEntityId: string;
  templateName: string;
  documentTagCode: string | null;
  recipientName: string;
  recipientEmail: string;
  originalFileName: string;
  sizeBytes: number;
  scanStatus: ScanStatus;
  reviewStatus: StagedReviewStatus;
  assessmentStatus: AssessmentRunStatus;
  packageDueDate: string | null;
  createdAt: string;
  filedDocumentId: string | null;
  // FL-12 — set when an LA bypassed the AV scan. When present the queue
  // badge renders "Scan overridden" (amber) instead of "Clean" (green).
  scanOverriddenAt: string | null;
  scanOverriddenById: string | null;
  // External Portal v2 PR 3 §10.5 — terminal state of the parent package
  // so the queue can render an "Archived" / "Cancelled" pill when the
  // toggle un-hides those rows. Optional for back-compat with older mocks.
  packageStatus?: CollectionPackageStatus;
  // External Portal v2 PR 3 §10.2 — days-since-assigned (computed from
  // CollectionPackage.SentAt). Null while the package is still a draft.
  daysSinceSent?: number | null;
  isStale?: boolean;
  // External Portal v2 PR 2 PROB-25a — review wait: days the submission has
  // sat awaiting review (from StagedSubmission.CreatedAt). Distinct from
  // daysSinceSent (request age). Drives the "Sent Xd · Wait Yd" chip.
  reviewWaitDays?: number | null;
  // External Portal v2 PR 3 §10.1 — effective internal assignee. Set from
  // the package's explicit AssignedToUserId when present; otherwise the
  // DealAssignment for the owner deal (preferred role first).
  effectiveAssignedToUserId?: string | null;
  effectiveAssignedToSource?: "Explicit" | "DealRole" | null;
  effectiveAssignedToRoleCode?: string | null;
  assignedToUserId?: string | null;
  // External Portal v2 PR 3 (PROB-24b) — every explicit assignee on the
  // package (many-to-many). Rendered as resolved-name chips; the queue falls
  // back to effectiveAssignedToUserId (DealRole) only when this is empty.
  assigneeUserIds?: string[];
  // Account-First — the recipient's linked portal account (PortalUser.Id),
  // when one has been minted. Lets the queue row deep-link into the account
  // detail review tab; null for legacy recipients.
  portalUserId?: string | null;
  // Guaranty Multi-Signer — the signer's name for a guaranty submission
  // (from the item's ESignEnvelope). Renders as a "Guarantor" chip so each
  // guaranty in a loan's package is identifiable by signer. Null otherwise.
  guarantorLabel?: string | null;
  // Review loop — the parent item's status and why the row is back in review.
  itemStatus?: CollectionItemStatus | null;
  reviewMarker?: StagedReviewMarker | null;
}

export interface StagedSubmissionDetail {
  id: string;
  collectionItemId: string;
  packageId: string;
  ownerEntityType: string;
  ownerEntityId: string;
  templateId: string;
  templateName: string;
  documentTagCode: string | null;
  templateInstructions: string | null;
  formSchemaJson: string | null;
  savedFormDataJson: string | null;
  kind: "Upload" | "Form" | "ESign" | "Education";
  isBrokerApplicationPersonalInformation: boolean;
  brokerApplicationTargetBrokerId: string | null;
  recipientId: string;
  recipientName: string;
  recipientEmail: string;
  recipientRoleLabel: string | null;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  scanStatus: ScanStatus;
  reviewStatus: StagedReviewStatus;
  assessmentStatus: AssessmentRunStatus;
  aiAssessmentMarkdown: string | null;
  hasAssessmentDocx: boolean;
  hasAssessmentPdf: boolean;
  reviewerNote: string | null;
  packageDueDate: string | null;
  createdAt: string;
  filedDocumentId: string | null;
  returnHistory: CollectionReturnComment[];
  // FL-12 — denormalized audit of the scan-override action, when one ran.
  // Null on every row whose scan resolved naturally.
  scanOverriddenAt: string | null;
  scanOverriddenById: string | null;
  scanOverrideReason: string | null;
  // External Portal v2 PR 3 §10.5 / §10.2 / §10.1 — package state + aging
  // + effective assignee in the detail payload so the review panel
  // doesn't need a second round-trip. Optional for back-compat with
  // older mock fixtures.
  packageStatus?: CollectionPackageStatus;
  daysSinceSent?: number | null;
  isStale?: boolean;
  // External Portal v2 PR 2 PROB-25a — review wait (days awaiting review).
  reviewWaitDays?: number | null;
  effectiveAssignedToUserId?: string | null;
  effectiveAssignedToSource?: "Explicit" | "DealRole" | null;
  effectiveAssignedToRoleCode?: string | null;
  assignedToUserId?: string | null;
  // External Portal v2 PR 3 (PROB-24b) — explicit assignees (many-to-many).
  assigneeUserIds?: string[];
  // Account-First — the recipient's linked portal account (PortalUser.Id),
  // when one has been minted. Lets the standalone review page offer an
  // "open in account view" link; null for legacy recipients.
  portalUserId?: string | null;
  // Staging recovery — when the current assessment run / scan was (re-)started
  // (elapsed-time + timeout clock) and the concise failure/error reason.
  assessmentStartedAt?: string | null;
  assessmentError?: string | null;
  scanStartedAt?: string | null;
  scanError?: string | null;
  // ---- Review loop ----
  // The parent item's status; the drawer leads with it.
  itemStatus?: CollectionItemStatus | null;
  // One chronological history across every submission for the item, newest
  // first. Replaces returnHistory on the client.
  reviewHistory?: CollectionReviewEvent[] | null;
  reviewMarker?: StagedReviewMarker | null;
  // The latest Return of THIS submission (banner).
  returnedByUserId?: string | null;
  returnedAt?: string | null;
  // The recipient owes a response to the latest return.
  responseOwed?: boolean;
  // "Reopen for review" availability, or the plain-English reason it's not.
  canReopen?: boolean;
  reopenBlockedReason?: string | null;
  // Package progress for the secondary line ("6 of 9 approved").
  packageItemsApproved?: number;
  packageItemsTotal?: number;
  // D9 — a Report Request row: labels only, no Returned banner or Reopen.
  isReportRequest?: boolean;
}

export interface StagedDestinationSuggestion {
  ownerEntityType: string;
  ownerEntityId: string;
  suggestedFolderId: string | null;
  suggestedFolderName: string | null;
  reason: string;
}

// Remediation Tier 2 Prompt 9-iii — quick-pick anchors for the per-item
// filing picker. The package owner always leads; each PortalUserAttachment
// on the recipient's external account follows so a single submission can
// be filed under any of the records the external party is attached to.
// folderId/folderName are populated when the entity already has a
// provisioned tree; otherwise the UI provisions via ensureEntityTree on
// first click.
export interface StagedDestinationOption {
  entityType: string;
  entityId: string;
  displayLabel: string;
  source: "Owner" | "Attachment";
  note: string | null;
  folderId: string | null;
  folderName: string | null;
}

export interface StagedDownload {
  url: string;
  expiresAt: string;
  // `url` is always the original file. For an Office source, `previewUrl` points
  // at a converted PDF and `isPdf` is true for inline preview.
  isPdf?: boolean;
  previewUrl?: string | null;
}

export interface BrokerApplicationImportResult {
  brokerId: string;
  stagedSubmissionId: string;
  importedAt: string;
}

/**
 * Mirrors PhoenixCapital.Core.DTOs.Collection.BrokerApplicationImportBlockReason —
 * a single reason the broker import was blocked. When `fieldKey` is set the panel
 * tags the offending reviewed-answers field (e.g. "state" / "primaryContactPhone")
 * so the reviewer can fix it inline. Returned in the ApiResponse `details` array
 * on a 400 from the import endpoint.
 */
export interface BrokerApplicationImportBlockReason {
  message: string;
  fieldKey?: string | null;
  label?: string | null;
}

// FL-8 — server-side sort keys for the staging queue list. Mirrors
// PhoenixCapital.Core.Interfaces.Collection.StagedSubmissionSortKey.
export type StagedSubmissionSortKey =
  | "CreatedAt"
  | "DueDate"
  | "Recipient"
  | "Owner"
  // External Portal v2 PR 2 PROB-25c — overdue first → approaching → newest.
  | "Urgency";

export interface StagingListQuery {
  ownerEntityType?: string;
  ownerEntityId?: string;
  recipientId?: string;
  documentTagCode?: string;
  overdueOnly?: boolean;
  includeReviewed?: boolean;
  reviewStatus?: StagedReviewStatus;
  // FL-8 — case-insensitive contains on recipient name or email.
  search?: string;
  sortBy?: StagedSubmissionSortKey;
  sortDescending?: boolean;
  // Account-First Phase 2 — narrows the queue to one package; backs the
  // inline review section on the package detail page.
  packageId?: string;
  // External Portal v2 PR 3 §10.5 — unhide submissions whose parent
  // package is Cancelled or Archived (default false).
  includeArchivedPackages?: boolean;
  // External Portal v2 PR 3 §10.1 — explicit-assignee filter.
  assignedToUserId?: string;
  // External Portal v2 PR 3 §10.1 — "Assigned to me" view. The server
  // substitutes the current user when true.
  assignedToMe?: boolean;
  // External Portal v2 PR 3 §10.1 — DealAssignment role-code filter
  // (e.g. "A1", "B1") for the owner deal.
  dealAssignmentRoleCode?: string;
  // Account-First — narrow to submissions whose recipient is linked to this
  // portal account (PortalUser.Id). Backs the account-detail review/completed
  // tabs, which reuse this list endpoint.
  portalUserId?: string;
  // UAT FixBatch-06 FIND-32 — offset/limit paging. Omitted by callers that want
  // the whole (small, scoped) set; the server defaults limit to 50.
  offset?: number;
  limit?: number;
}

// UAT FixBatch-06 FIND-32 — paginated staging-queue response.
export interface StagedSubmissionListResponse {
  items: StagedSubmissionListItem[];
  total: number;
}

export const collectionStagingApi = {
  // UAT FixBatch-06 FIND-32 — returns { items, total }. The main queue pages it
  // via offset/limit; scoped callers (package / account tabs) read `.items`.
  list: (query: StagingListQuery = {}) =>
    api.get<StagedSubmissionListResponse>("/collection/staging", query),
  get: (id: string) =>
    api.get<StagedSubmissionDetail>(`/collection/staging/${id}`),
  downloadUrl: (id: string) =>
    api.get<StagedDownload>(`/collection/staging/${id}/download-url`),
  assessmentDownloadUrl: (id: string, kind: "pdf" | "docx") =>
    api.get<StagedDownload>(`/collection/staging/${id}/assessment-download-url`, {
      kind,
    }),
  suggestedFolder: (id: string) =>
    api.get<StagedDestinationSuggestion>(`/collection/staging/${id}/suggested-folder`),
  destinationOptions: (id: string) =>
    api.get<StagedDestinationOption[]>(`/collection/staging/${id}/destination-options`),
  updateFormData: (id: string, savedFormDataJson: string) =>
    api.put<StagedSubmissionDetail>(`/collection/staging/${id}/form-data`, { savedFormDataJson }),
  importBrokerApplication: (id: string) =>
    api.post<BrokerApplicationImportResult>(`/collection/staging/${id}/import-broker-application`, {}),
  approve: (id: string) =>
    api.post<StagedSubmissionDetail>(`/collection/staging/${id}/approve`),
  file: (id: string, folderId: string, linkAiDistillation = true) =>
    api.post<StagedSubmissionDetail>(`/collection/staging/${id}/file`, {
      folderId,
      linkAiDistillation,
    }),
  return: (id: string, note: string) =>
    api.post<StagedSubmissionDetail>(`/collection/staging/${id}/return`, { note }),
  // Review loop — put a Returned submission back into review without waiting
  // on the recipient. The note is required (400 when blank or > 4000 chars, or
  // when the row can't be reopened); 409 on a race. Sends no email.
  reopen: (id: string, note: string) =>
    api.post<StagedSubmissionDetail>(`/collection/staging/${id}/reopen`, { note }),
  overrideScan: (id: string, reason: string) =>
    api.post<StagedSubmissionDetail>(`/collection/staging/${id}/override-scan`, { reason }),
  // Staging recovery — manual recovery controls for stuck async jobs.
  regenerateAssessment: (id: string) =>
    api.post<StagedSubmissionDetail>(`/collection/staging/${id}/regenerate-assessment`, {}),
  refreshScan: (id: string) =>
    api.post<StagedSubmissionDetail>(`/collection/staging/${id}/refresh-scan`, {}),
};
